import React, { useMemo, useRef, useState } from 'react'
import type { ColumnDefinition, ColumnFilterOption } from '../types'
import { getColumnLabel } from '../types'
import * as s from '../Table.css'
import { TablePopover } from './TablePopover'
import { DateRangeChip } from './DateRangeChip'
import { EntityFilterChip } from './EntityFilterChip'

function Icon({ name, size = 14 }: { name: string; size?: number }) {
  const p = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  }
  if (name === 'plus') {
    return (
      <svg {...p}>
        <path d="M12 5v14M5 12h14" />
      </svg>
    )
  }
  return null
}

function resolveOptions<TRow>(
  column: ColumnDefinition<TRow>,
  allRows: TRow[],
): ColumnFilterOption[] {
  if (!column.filterOptions) return []
  return typeof column.filterOptions === 'function'
    ? column.filterOptions(allRows)
    : column.filterOptions
}

export function FilterToolbarChip<TRow>({
  column,
  allRows,
  value,
  onChange,
  showCounts = true,
  presentation = 'popover',
}: {
  column: ColumnDefinition<TRow>
  allRows: TRow[]
  value: string[] | null
  onChange: (v: string[] | null) => void
  /** Hide option counts (e.g. server-side tables). */
  showCounts?: boolean
  /**
   * `inline` expands options below the trigger (for modals).
   * `popover` uses a portaled dropdown (toolbar).
   */
  presentation?: 'popover' | 'inline'
}) {
  // All hooks must run unconditionally before any early return
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const isSelect = column.filterType === 'select'
  const inline = presentation === 'inline'

  const options = useMemo(() => resolveOptions(column, allRows), [allRows, column])
  const accessor =
    column.filterAccessor ??
    ((r: TRow) => String((r as Record<string, unknown>)[column.id] ?? ''))

  const counts = useMemo(() => {
    if (!showCounts) return {}
    const m: Record<string, number> = {}
    for (const r of allRows) {
      const v = accessor(r)
      const keys = Array.isArray(v) ? v : [v]
      for (const key of keys) {
        if (!key) continue
        m[key] = (m[key] ?? 0) + 1
      }
    }
    return m
  }, [allRows, accessor, showCounts])

  const active = (value ?? []).length > 0
  const label = column.filterLabel ?? getColumnLabel(column)

  const toggle = (v: string) => {
    if (isSelect) {
      onChange(value?.[0] === v ? null : [v])
      return
    }
    const set = new Set(value ?? [])
    if (set.has(v)) set.delete(v)
    else set.add(v)
    onChange(set.size ? [...set] : null)
  }

  if (column.filterType === 'daterange') {
    return (
      <DateRangeChip
        label={label}
        value={value}
        onChange={onChange}
        presentation={presentation}
      />
    )
  }

  if (column.filterType === 'entity' && column.entityFilter) {
    return (
      <EntityFilterChip
        label={label}
        value={value}
        onChange={onChange}
        config={column.entityFilter}
        presentation={presentation}
      />
    )
  }

  const valueSummary = active
    ? value!.length === 1
      ? (options.find((o) => o.value === value![0])?.label ?? value![0])
      : `${value!.length} selected`
    : null

  const panel = (
    <>
      <div className={s.popoverOptionList}>
        {options.map((opt) => (
          <label
            key={opt.value}
            className={s.popoverOption}
            title={opt.title ?? (opt.label !== opt.value ? opt.value : undefined)}
          >
            <input
              type={isSelect ? 'radio' : 'checkbox'}
              className={s.checkbox}
              checked={(value ?? []).includes(opt.value)}
              onChange={() => toggle(opt.value)}
            />
            <span style={{ flex: 1 }}>{opt.label}</span>
            {showCounts && (
              <span className={s.popoverCount}>{counts[opt.value] ?? 0}</span>
            )}
          </label>
        ))}
      </div>
      {active && (
        <>
          <div className={s.popoverDivider} />
          <div className={s.popoverFoot}>
            <button type="button" className={s.popoverFootBtn} onClick={() => onChange(null)}>
              Clear
            </button>
            <button type="button" className={s.popoverFootBtn} onClick={() => setOpen(false)}>
              Done
            </button>
          </div>
        </>
      )}
    </>
  )

  if (inline) {
    return (
      <div className={s.filterSheetControl}>
        <button
          ref={anchorRef}
          type="button"
          className={`${s.sortConfigBtn} ${s.filterSheetTrigger}${open || active ? ' is-active' : ''}`}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          {!active && <Icon name="plus" size={12} />}
          <span className={s.filterChipLabel}>{label}</span>
          {valueSummary ? (
            <span className={s.filterSheetTriggerValue}>{valueSummary}</span>
          ) : null}
        </button>
        {open ? <div className={s.filterSheetInlinePanel}>{panel}</div> : null}
      </div>
    )
  }

  return (
    <div className={s.popoverAnchor}>
      <button
        ref={anchorRef}
        type="button"
        className={`${s.filterChip}${active ? ' is-active' : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        {!active && <Icon name="plus" size={12} />}
        <span className={s.filterChipLabel}>{label}</span>
        {active && (
          <span className={s.filterChipValues}>
            {value!.length === 1 ? (
              <span>{options.find((o) => o.value === value![0])?.label ?? value![0]}</span>
            ) : (
              <span className={s.filterChipPill}>{value!.length}</span>
            )}
          </span>
        )}
      </button>
      <TablePopover anchorRef={anchorRef} open={open} onClose={() => setOpen(false)}>
        {panel}
      </TablePopover>
    </div>
  )
}
